<template>
  <div class="op-modal-backdrop" @click.self="$emit('close')">
    <div class="op-sheet">
      <div class="op-sheet__head">
        <h3 class="op-sheet__title">
          <i :class="def.icon"></i> {{ def.label }}
        </h3>
        <button class="op-sheet__close" @click="$emit('close')">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <div class="op-sheet__body">
        <!-- Farmaco: selezione terapia esistente (mai libera) -->
        <template v-if="def.therapy">
          <div v-if="!therapies.length" class="op-empty">
            Nessuna terapia attiva disponibile per questo ospite.
          </div>
          <div v-else class="op-field">
            <label>Terapia / farmaco</label>
            <select v-model.number="form.therapyId" class="op-input">
              <option
                v-for="t in therapies"
                :key="t.therapyId"
                :value="t.therapyId"
              >
                {{ t.scheduledTime }} — {{ t.medicationName }} ({{
                  t.doseAmount
                }}
                {{ t.doseUnit }})
              </option>
            </select>
          </div>
        </template>

        <!-- Cambio panno: panni + traverse (scarico magazzino separato) -->
        <template v-if="def.supply">
          <div v-if="!absorbentPads.length && !bedPads.length" class="op-empty">
            Nessuna fornitura configurata: il cambio sarà registrato senza
            scarico magazzino.
          </div>

          <div v-if="absorbentPads.length" class="op-field">
            <label>Panno / pannolone</label>
            <select v-model.number="form.supplyItemId" class="op-input">
              <option :value="null">Nessuno</option>
              <option v-for="p in absorbentPads" :key="p.id" :value="p.id">
                {{ p.name }} — disp. {{ p.currentStock }} {{ p.unit }}
              </option>
            </select>
            <template v-if="form.supplyItemId">
              <label style="margin-top: 8px">Quantità panni</label>
              <input
                v-model.number="form.quantity"
                type="number"
                min="1"
                class="op-input"
              />
              <small v-if="padShort" class="op-warn">
                Stock insufficiente: l'anomalia sarà segnalata nella review
                stock.
              </small>
            </template>
          </div>

          <div v-if="bedPads.length" class="op-field">
            <label>Traversa</label>
            <select v-model.number="form.bedPadItemId" class="op-input">
              <option :value="null">Nessuna</option>
              <option v-for="b in bedPads" :key="b.id" :value="b.id">
                {{ b.name }} — disp. {{ b.currentStock }} {{ b.unit }}
              </option>
            </select>
            <template v-if="form.bedPadItemId">
              <label style="margin-top: 8px">Quantità traverse</label>
              <input
                v-model.number="form.bedPadQuantity"
                type="number"
                min="0"
                class="op-input"
              />
              <small v-if="bedShort" class="op-warn">
                Stock insufficiente: l'anomalia sarà segnalata nella review
                stock.
              </small>
            </template>
          </div>
        </template>

        <!-- Pasto -->
        <div v-if="def.meal" class="op-field">
          <label>Pasto</label>
          <div class="op-chips">
            <button
              v-for="(lbl, key) in mealLabels"
              :key="key"
              type="button"
              class="op-chip"
              :class="{ 'op-chip--on': form.mealType === key }"
              @click="form.mealType = key"
            >
              {{ lbl }}
            </button>
          </div>
        </div>

        <!-- Campi numerici (vitali) -->
        <div v-for="f in def.fields || []" :key="f.key" class="op-field">
          <label>{{ f.label }}</label>
          <input
            v-model.number="form[f.key]"
            type="number"
            inputmode="decimal"
            class="op-input"
          />
        </div>

        <!-- Momento misurazione (glicemia) -->
        <div v-if="def.moment" class="op-field">
          <label>Momento</label>
          <select v-model="form.measurementMoment" class="op-input">
            <option v-for="(lbl, key) in moments" :key="key" :value="key">
              {{ lbl }}
            </option>
          </select>
        </div>

        <!-- Ossigeno (saturazione) -->
        <div v-if="def.oxygen" class="op-field">
          <label class="op-checkbox">
            <input v-model="form.oxygen" type="checkbox" />
            In ossigenoterapia
          </label>
        </div>

        <!-- Stato (azioni non-vitali) -->
        <div v-if="!def.vital" class="op-field">
          <label>Esito</label>
          <div class="op-chips">
            <button
              v-for="s in def.statuses"
              :key="s"
              type="button"
              class="op-chip"
              :class="{ 'op-chip--on': form.status === s }"
              @click="form.status = s"
            >
              {{ statusLabel(s) }}
            </button>
          </div>
        </div>

        <!-- Note -->
        <div class="op-field">
          <label>Note (opzionale)</label>
          <textarea
            v-model.trim="form.notes"
            rows="2"
            class="op-input"
          ></textarea>
        </div>
      </div>

      <div class="op-sheet__actions">
        <button class="villa-btn villa-btn--ghost" @click="$emit('close')">
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--primary"
          :disabled="saving || (def.therapy && !therapies.length)"
          @click="save"
        >
          <i v-if="saving" class="fa-solid fa-spinner fa-spin"></i>
          Conferma
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import {
  MEAL_LABELS,
  MEASUREMENT_MOMENTS,
  statusLabel,
} from "@/utils/careActions";

export default {
  name: "OperatorActionModal",
  props: {
    def: { type: Object, required: true },
    guestId: { type: [String, Number], required: true },
    facilityId: { type: [String, Number], required: true },
    therapies: { type: Array, default: () => [] },
    absorbentPads: { type: Array, default: () => [] },
    bedPads: { type: Array, default: () => [] },
  },
  emits: ["close", "saved"],
  data() {
    return {
      saving: false,
      mealLabels: MEAL_LABELS,
      moments: MEASUREMENT_MOMENTS,
      form: {
        status: this.def.vital ? "completed" : this.def.statuses[0],
        mealType: this.def.meal ? "lunch" : null,
        therapyId:
          this.def.therapy && this.therapies[0]
            ? this.therapies[0].therapyId
            : null,
        measurementMoment: this.def.moment ? "fasting" : null,
        oxygen: false,
        supplyItemId:
          this.def.supply && this.absorbentPads[0]
            ? this.absorbentPads[0].id
            : null,
        quantity: 1,
        bedPadItemId:
          this.def.supply && this.bedPads[0] ? this.bedPads[0].id : null,
        bedPadQuantity: 0,
        numericValue: null,
        numericValue2: null,
        numericValue3: null,
        notes: "",
      },
    };
  },
  computed: {
    padShort() {
      const it = this.absorbentPads.find(
        (p) => p.id === this.form.supplyItemId
      );
      return !!it && Number(this.form.quantity) > Number(it.currentStock);
    },
    bedShort() {
      const it = this.bedPads.find((b) => b.id === this.form.bedPadItemId);
      return (
        !!it &&
        Number(this.form.bedPadQuantity) > 0 &&
        Number(this.form.bedPadQuantity) > Number(it.currentStock)
      );
    },
  },
  methods: {
    ...mapActions(["createCareActivity"]),
    statusLabel,
    buildPayload() {
      const p = {
        facilityId: Number(this.facilityId),
        actionType: this.def.type,
        status: this.form.status,
        notes: this.form.notes || "",
      };
      if (this.def.meal) p.mealType = this.form.mealType;
      if (this.def.therapy) p.therapyId = this.form.therapyId;
      if (this.def.supply) {
        if (this.form.supplyItemId) {
          p.supplyItemId = this.form.supplyItemId;
          p.quantity =
            Number(this.form.quantity) > 0 ? Number(this.form.quantity) : 1;
        }
        if (this.form.bedPadItemId && Number(this.form.bedPadQuantity) > 0) {
          p.bedPadItemId = this.form.bedPadItemId;
          p.bedPadQuantity = Number(this.form.bedPadQuantity);
        }
      }
      if (this.def.fields) {
        for (const f of this.def.fields) {
          if (this.form[f.key] !== null && this.form[f.key] !== "") {
            p[f.key] = Number(this.form[f.key]);
          }
        }
      }
      const metadata = {};
      if (this.def.moment)
        metadata.measurementMoment = this.form.measurementMoment;
      if (this.def.oxygen) metadata.oxygen = !!this.form.oxygen;
      if (Object.keys(metadata).length) p.metadata = metadata;
      return p;
    },
    validate() {
      if (this.def.therapy && !this.form.therapyId) {
        return "Seleziona una terapia esistente.";
      }
      if (this.def.fields) {
        for (const f of this.def.fields) {
          if (
            !f.optional &&
            (this.form[f.key] === null || this.form[f.key] === "")
          ) {
            return `Inserisci: ${f.label}.`;
          }
        }
      }
      return "";
    },
    async save() {
      const err = this.validate();
      if (err) return this.$toast.error(err);
      this.saving = true;
      try {
        const activity = await this.createCareActivity({
          guestId: this.guestId,
          payload: this.buildPayload(),
        });
        this.$toast.success(`${this.def.label}: registrata.`);
        // Stock insufficiente (farmaci o presidi): attività salvata ma "da verificare".
        if (
          activity &&
          activity.status === "to_verify" &&
          activity.metadata &&
          (activity.metadata.stockWarning || activity.metadata.supplyWarnings)
        ) {
          this.$toast.error(
            "Stock insufficiente: attività registrata come 'da verificare'."
          );
        }
        this.$emit("saved", activity);
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message || "Registrazione non riuscita."
        );
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.op-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(8, 43, 87, 0.4);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1200;
}
.op-sheet {
  background: #fff;
  width: 100%;
  max-width: 560px;
  border-radius: 18px 18px 0 0;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}
@media (min-width: 640px) {
  .op-modal-backdrop {
    align-items: center;
  }
  .op-sheet {
    border-radius: 16px;
  }
}
.op-sheet__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid #eef3f9;
}
.op-sheet__title {
  margin: 0;
  font-size: 18px;
  color: var(--villa-navy, #082b57);
}
.op-sheet__title i {
  margin-right: 8px;
}
.op-sheet__close {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #64748b;
}
.op-sheet__body {
  padding: 16px 18px;
  overflow-y: auto;
}
.op-field {
  margin-bottom: 16px;
}
.op-field > label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}
.op-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 12px;
  font-size: 16px;
  font-family: inherit;
}
.op-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.op-chip {
  border: 1px solid var(--villa-azure, #dceaf7);
  background: #fff;
  border-radius: 999px;
  padding: 10px 16px;
  font-size: 14px;
  cursor: pointer;
}
.op-chip--on {
  background: var(--villa-navy, #082b57);
  color: #fff;
  border-color: var(--villa-navy, #082b57);
}
.op-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 500 !important;
}
.op-empty {
  background: #fff7ed;
  color: #9a3412;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  padding: 12px;
  font-size: 14px;
  margin-bottom: 12px;
}
.op-warn {
  display: block;
  margin-top: 6px;
  color: #b45309;
  font-size: 12px;
}
.op-sheet__actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 14px 18px;
  border-top: 1px solid #eef3f9;
}
</style>
